import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Flex, Grid } from "theme-ui";
import moment from "moment-timezone";
import { Form, Formik } from "formik";
import * as Yup from "yup";

import { Modal, ModalHeader, ModalBody, ModalFooter } from "components/ui/Modal";
import BasicButton from "components/forms/BasicButton";
import TextInput from "components/forms/TextInput";
import SelectDate from "components/forms/SelectDate";
import Span from "components/typography/Span";
import Subtext from "components/typography/Subtext";
import { CheckMarkIcon } from "components/svg";
import {
  mnt,
  mntCard,
  mntPill,
  mntProgressTrack,
  mntProgressFill,
} from "theme/maintenanceTokens";
import type { MntStatus } from "theme/maintenanceTokens";
import { useToast } from "components/toast";

import {
  createPlanGroup,
  updatePlanGroup,
  getGroupStats,
} from "api/ServicePlans";

import PlanSectionHeader from "./PlanSectionHeader";

/**
 * MASTER_PLAN.md §3.9 #22 — the Work Order Groups section on Plan
 * Detail (mockup 13 middle of main column). User-facing label is
 * "Batches" per open decision #11 — code retains the "Group" noun to
 * match the backend `work_order_group` table.
 *
 * SESSION 46 REWRITE — batch allocation moved into the Locations table.
 *
 * Through Session 45 this section owned a roster drawer: each card had
 * a "Locations" toggle that opened an inline panel listing the batch's
 * roster rows, with per-row "Create WO" and "Remove" and a bulk
 * "Add Locations" modal. That duplicated the Locations table sitting
 * directly below it. Allocation now happens in that table via its
 * "Select to batch" multi-select + bulk "Add to batch" bar, and
 * raising a WO for a Location happens from the table row's inline
 * expand. This section is now just the batch cards: create, read
 * progress, edit. The roster drawer, its AllocateModal, and its
 * QuickWorkOrderModal are retired (see PLAN_DETAIL_INTERACTION_MODEL.md
 * and OPERATING_MODEL.md).
 *
 * Progress numbers come from the backend (`GET /groups/:id/stats`)
 * rather than being recomputed client-side from the sites list, which
 * double-counted across batches. Stats load per card and fall back to
 * zeroed counts if the endpoint fails, so a stats outage degrades the
 * card rather than blanking the section.
 *
 * Two lint/type constraints this file has already tripped, noted so
 * the next edit doesn't reintroduce them:
 *   - `Box as="button"` is theme-ui's Box; its props are BoxProps, NOT
 *     ButtonHTMLAttributes. No `disabled` prop. Guard the click handler
 *     and set `pointerEvents: none` instead.
 *   - `@typescript-eslint/no-non-null-assertion` is on and CI-fatal.
 *     No `foo!.bar` — use optional chaining even where the value is
 *     provably non-null in context.
 */
export type WorkOrderGroup = {
  id: string;
  name: string;
  committedAt?: string | null;
  targetDate?: string | null;
  committedCount?: number;
  notes?: string | null;
  createdAt: string;
};

export type GroupStats = {
  committedCount: number;
  rosterSize: number;
  planned: number;
  woCreated: number;
  serviced: number;
  progressPct: number;
  /** P-c — server-computed live work signal. Present on APIs from
   * Session 55 onward; absent on older ones, in which case the card
   * falls back to the counts the parent derived from the plan's WO
   * list. */
  openWorkOrders?: number;
  pastSla?: number;
};

const EMPTY_STATS: GroupStats = {
  committedCount: 0,
  rosterSize: 0,
  planned: 0,
  woCreated: 0,
  serviced: 0,
  progressPct: 0,
};

export default function WorkOrderGroupsSection({
  planId,
  groups,
  canEdit,
  intakeStyle,
  woStatsByGroup,
  stageDistByGroup,
  selectedGroupId,
  onSelectGroup,
  onGroupCreated,
  onGroupEdited,
}: {
  planId: string;
  groups: WorkOrderGroup[];
  canEdit: boolean;
  /** OPERATING_MODEL.md — batches organize Work Orders, so the whole
   * section is Work-Order-fed only. Quote-fed plans render nothing
   * here. Undefined (older payloads) is treated as quote-fed. */
  intakeStyle?: "work_order_fed" | "quote_fed";
  /** P-c — live work signal per batch, keyed by group id. Computed by
   * the parent from the plan's WO list rather than fetched: the WOs are
   * already loaded there, and `GET /groups/:id/stats` does not carry
   * open/overdue counts. A batch you cannot see the live work on is a
   * label, not a cohort you manage against. */
  woStatsByGroup?: Record<string, { open: number; overdue: number }>;
  /** P-j — per-batch work-order stage counts, so progress is legible per
   * cohort. A batch at 14% complete with nothing in flight and a batch
   * at 14% with eleven work orders moving are different situations, and
   * the progress bar alone cannot tell them apart. */
  stageDistByGroup?: Record<string, Record<string, number>>;
  /** P-i — the selected batch scopes the page. Null means the whole
   * plan. */
  selectedGroupId?: string | null;
  onSelectGroup?: (groupId: string | null) => void;
  onGroupCreated: (group: WorkOrderGroup) => void;
  onGroupEdited?: (group: WorkOrderGroup) => void;
}) {
  const [showCreate, setShowCreate] = useState(false);
  const [editingGroup, setEditingGroup] = useState<WorkOrderGroup | null>(null);
  const [statsByGroup, setStatsByGroup] = useState<Record<string, GroupStats>>({});

  const groupIds = useMemo(() => groups.map((g) => g.id).join(","), [groups]);

  const loadStats = useCallback(async () => {
    if (!groups.length) return;
    const results = await Promise.all(
      groups.map((g) => getGroupStats({ data: { planId, groupId: g.id } })),
    );
    const next: Record<string, GroupStats> = {};
    groups.forEach((g, i) => {
      next[g.id] = results[i] || EMPTY_STATS;
    });
    setStatsByGroup(next);
    // eslint-disable-next-line
  }, [planId, groupIds]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // OPERATING_MODEL.md — batches are a Work-Order-fed concept. A
  // quote-fed plan (church/HOA and most clients) has no work orders to
  // batch, so the section renders nothing at all. Guard first, before
  // any empty-state or create affordance.
  if (intakeStyle !== "work_order_fed") return null;

  if (groups.length === 0) {
    if (!canEdit) return null; // client-side view — nothing to show
    return (
      <>
        <SectionHeader canEdit={canEdit} count={0} onCreate={() => setShowCreate(true)} />
        <Box
          sx={{
            ...mntCard,
            border: `1px dashed ${mnt.divider}`,
            padding: "18px 20px",
            marginBottom: "24px",
          }}
        >
          <Subtext fontSize={"12px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
            Optional. Batches let you commit chunks of a plan on a schedule (e.g. FY27 Batch 1 = 125 Locations due Oct 31), then work those Locations down as the client releases them. Skip for one-off / HOA-shape plans.
          </Subtext>
        </Box>
        {showCreate && (
          <GroupModal
            planId={planId}
            existing={null}
            onClose={() => setShowCreate(false)}
            onSaved={(g) => {
              setShowCreate(false);
              onGroupCreated(g);
            }}
          />
        )}
      </>
    );
  }

  return (
    <>
      <SectionHeader canEdit={canEdit} count={groups.length} onCreate={() => setShowCreate(true)} />
      {canEdit && (
        <Subtext
          fontSize={"11px"}
          fontFamily={"Poppins"}
          margin={0}
          marginBottom={"10px"}
          color={mnt.textMuted}
        >
          Allocate Locations to a batch from the Locations table below: select rows with their checkboxes, then use the bulk bar. Filter the table to one batch with its chip.
        </Subtext>
      )}
      {/* Session 56 (mockup 29) — batches are a compact selectable
       * strip, not tall cards. They tag work orders; they should not
       * dominate the page above the work itself. Detail moves to the
       * chip's tooltip and to the scoped sections below. Wraps to rows
       * when a plan carries many batches. */}
      <Flex
        sx={{
          flexWrap: "wrap",
          gap: "10px",
          marginBottom: "24px",
        }}
      >
        {groups.map((g) => (
          <GroupCard
            key={g.id}
            group={g}
            stats={statsByGroup[g.id] || EMPTY_STATS}
            woStats={woStatsByGroup?.[g.id]}
            stageDist={stageDistByGroup?.[g.id]}
            selected={selectedGroupId === g.id}
            onSelect={
              onSelectGroup
                ? () => onSelectGroup(selectedGroupId === g.id ? null : g.id)
                : undefined
            }
            canEdit={canEdit}
            onEdit={() => setEditingGroup(g)}
          />
        ))}
      </Flex>

      {showCreate && (
        <GroupModal
          planId={planId}
          existing={null}
          onClose={() => setShowCreate(false)}
          onSaved={(g) => {
            setShowCreate(false);
            onGroupCreated(g);
          }}
        />
      )}
      {editingGroup && (
        <GroupModal
          planId={planId}
          existing={editingGroup}
          onClose={() => setEditingGroup(null)}
          onSaved={(g) => {
            setEditingGroup(null);
            if (onGroupEdited) onGroupEdited(g);
            loadStats();
          }}
        />
      )}
    </>
  );
}

function SectionHeader({
  count,
  canEdit,
  onCreate,
}: {
  count: number;
  canEdit: boolean;
  onCreate: () => void;
}) {
  return (
    <PlanSectionHeader
      label="Batches"
      hint={
        count > 0
          ? `optional \u00b7 ${count} configured \u00b7 click one to scope the page`
          : "optional"
      }
      action={
        canEdit ? (
          <Box
            as="button"
            onClick={onCreate}
            sx={{
              background: "transparent",
              border: "none",
              padding: 0,
              fontSize: "12px",
              fontFamily: "Poppins",
              color: mnt.link,
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            + New batch
          </Box>
        ) : null
      }
    />
  );
}

function GroupCard({
  group,
  stats,
  woStats,
  stageDist,
  selected,
  onSelect,
  canEdit,
  onEdit,
}: {
  group: WorkOrderGroup;
  stats: GroupStats;
  woStats?: { open: number; overdue: number };
  stageDist?: Record<string, number>;
  selected?: boolean;
  onSelect?: () => void;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const denom = stats.committedCount > 0 ? stats.committedCount : stats.rosterSize;
  const barPct = Math.max(0, Math.min(100, stats.progressPct));

  // P-c — prefer the counts the parent derived from the plan's loaded
  // work-order list (it knows the derived stages), and fall back to the
  // server's own counts so a batch card is truthful even when mounted
  // somewhere that has not loaded that list.
  const liveWork =
    woStats ??
    (stats.openWorkOrders != null
      ? { open: Number(stats.openWorkOrders), overdue: Number(stats.pastSla ?? 0) }
      : undefined);

  const daysRemaining = group.targetDate
    ? moment(group.targetDate).diff(moment(), "days")
    : null;

  const statusPill: { label: string; status: MntStatus } = (() => {
    if (denom > 0 && stats.serviced >= denom) {
      return { label: "Complete", status: "current" };
    }
    if (stats.rosterSize === 0) {
      return { label: "Not allocated", status: "ungrouped" };
    }
    if (stats.serviced === 0 && stats.woCreated === 0) {
      return { label: "Upcoming", status: "upcoming" };
    }
    if (daysRemaining !== null && daysRemaining < 30 && stats.progressPct < 80) {
      return { label: "At risk", status: "overdue" };
    }
    return { label: "On track", status: "onTrack" };
  })();

  const targetLine = (() => {
    const parts: string[] = [];
    if (group.committedAt) parts.push(`Committed ${moment(group.committedAt).format("MMM D")}`);
    if (group.targetDate) parts.push(`target ${moment(group.targetDate).format("MMM D, YYYY")}`);
    return parts.join(" · ");
  })();

  // SESSION 46 DRIFT-3 — one stat line, mockup shape:
  // `18 of 125 serviced · 14% · 101 days remaining`.
  const progressLine = (() => {
    if (denom === 0) return "No Locations allocated yet";
    const suffix =
      daysRemaining !== null
        ? daysRemaining > 0
          ? ` · ${daysRemaining} days remaining`
          : daysRemaining === 0
            ? " · due today"
            : ` · ${Math.abs(daysRemaining)} days overdue`
        : "";
    return `${stats.serviced} of ${denom} serviced · ${stats.progressPct}%${suffix}`;
  })();

  // Allocation breakdown moves off the card face (DRIFT-3) to a
  // native hover tooltip; the roster panel carries it in full.
  const allocationTitle = (() => {
    const parts = [`${stats.rosterSize} allocated`];
    if (stats.planned) parts.push(`${stats.planned} awaiting release`);
    if (stats.woCreated) parts.push(`${stats.woCreated} in flight`);
    if (stats.committedCount > 0 && stats.rosterSize < stats.committedCount) {
      parts.push(`${stats.committedCount - stats.rosterSize} unallocated`);
    }
    return parts.join(" · ");
  })();

  const tone = selected ? mnt.onInkMuted : mnt.textMuted;
  // Detail that used to sit on the card face now rides the chip's
  // native tooltip, so the strip stays compact without losing it.
  const tooltip = [targetLine, allocationTitle, progressLine]
    .filter(Boolean)
    .join(" \u2014 ");

  return (
    <Box
      title={tooltip}
      onClick={onSelect}
      sx={{
        ...mntCard,
        ...(selected ? { background: mnt.ink } : {}),
        border: selected ? `1.5px solid ${mnt.ink}` : mntCard.border,
        borderRadius: "8px",
        padding: "10px 12px",
        width: "200px",
        flex: "0 0 auto",
        cursor: onSelect ? "pointer" : "default",
      }}
    >
      <Flex sx={{ justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
        <Span
          fontFamily={"Poppins"}
          fontWeight={600}
          fontSize={"12.5px"}
          sx={{
            color: selected ? mnt.onInk : undefined,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {group.name}
        </Span>
        <Box
          sx={{
            ...mntPill(statusPill.status),
            fontSize: "9px",
            padding: "1px 7px",
            flex: "0 0 auto",
          }}
        >
          {statusPill.label}
        </Box>
      </Flex>

      {stageDist ? (
        <StageDistribution dist={stageDist} compact onDark={selected} />
      ) : (
        <Box
          sx={{
            ...mntProgressTrack,
            ...(selected ? { background: "#4A4A47" } : {}),
            marginTop: "8px",
            height: "6px",
          }}
        >
          <Box sx={mntProgressFill(barPct)} />
        </Box>
      )}

      <Flex
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "6px",
          gap: "6px",
        }}
      >
        <Subtext fontSize={"10.5px"} fontFamily={"Poppins"} margin={0} color={tone}>
          {denom === 0
            ? "no Locations yet"
            : `${stats.serviced}/${denom} \u00b7 ${stats.progressPct}%`}
          {/* SESSION 69 - the serviced count alone reads as "this batch
              does not know about its work orders" when nothing has been
              serviced yet but dozens of sites have live WOs. `serviced`
              is a COMPLETION measure and only moves at the very end of
              the pipeline, so early in a batch's life the card face said
              0/125 while 49 sites were in flight - that signal was only
              on the hover tooltip. Surface it. */}
          {stats.woCreated > 0 ? ` \u00b7 ${stats.woCreated} in flight` : ""}
          {daysRemaining !== null
            ? daysRemaining >= 0
              ? ` \u00b7 ${daysRemaining}d left`
              : ` \u00b7 ${Math.abs(daysRemaining)}d over`
            : ""}
        </Subtext>
        {canEdit && (
          <Box
            as="button"
            onClick={(e: any) => {
              e.stopPropagation();
              onEdit();
            }}
            sx={{
              background: "transparent",
              border: "none",
              padding: 0,
              fontSize: "10.5px",
              fontFamily: "Poppins",
              color: selected ? mnt.onInkMuted : mnt.link,
              cursor: "pointer",
              flex: "0 0 auto",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Edit
          </Box>
        )}
      </Flex>

      {liveWork && liveWork.overdue > 0 && (
        <Span
          fontFamily={"Poppins"}
          fontSize={"10.5px"}
          sx={{
            color: selected ? "#F5B5B5" : mnt.red,
            fontWeight: 600,
            display: "block",
            marginTop: "3px",
          }}
        >
          {liveWork.overdue} past SLA
        </Span>
      )}
    </Box>
  );
}

/**
 * P-j — a batch's work orders, by stage, as a segmented bar plus a
 * counts line. Only stages actually present are drawn, so a quiet batch
 * shows a short bar rather than five empty segments pretending to be
 * data. Renders nothing when the batch has no work orders at all.
 */
function StageDistribution({
  dist,
  compact,
  onDark,
}: {
  dist: Record<string, number>;
  compact?: boolean;
  onDark?: boolean;
}) {
  const SEGMENTS: Array<{ key: string; label: string; color: string }> = [
    { key: "needs_match", label: "need match", color: mnt.red },
    { key: "rfq_generated", label: "need RFQ", color: mnt.amber },
    { key: "quote_sent", label: "quoted", color: mnt.corrigoPillFg },
    { key: "accepted", label: "accepted", color: mnt.teal },
    { key: "in_production", label: "in production", color: mnt.green },
    { key: "invoiced", label: "invoiced", color: mnt.textMuted },
  ];

  const present = SEGMENTS.filter((s) => (dist[s.key] ?? 0) > 0);
  const total = present.reduce((sum, s) => sum + (dist[s.key] ?? 0), 0);
  if (total === 0) return null;

  return (
    <Box sx={{ marginTop: "8px" }}>
      <Flex
        sx={{
          height: compact ? "6px" : "8px",
          borderRadius: "4px",
          overflow: "hidden",
          background: onDark ? "#4A4A47" : mnt.bgBeige,
        }}
      >
        {present.map((s) => (
          <Box
            key={s.key}
            title={`${dist[s.key]} ${s.label}`}
            sx={{
              width: `${((dist[s.key] ?? 0) / total) * 100}%`,
              background: s.color,
            }}
          />
        ))}
      </Flex>
      {!compact && (
        <Subtext
          fontSize={"10px"}
          fontFamily={"Poppins"}
          margin={0}
          marginTop={"4px"}
          color={mnt.textMuted}
        >
          {present.map((s) => `${dist[s.key]} ${s.label}`).join(" \u00b7 ")}
        </Subtext>
      )}
    </Box>
  );
}

// ─── Create / Edit Batch modal (inline, small) ─────────────────────

function GroupModal({
  planId,
  existing,
  onClose,
  onSaved,
}: {
  planId: string;
  existing: WorkOrderGroup | null;
  onClose: () => void;
  onSaved: (g: WorkOrderGroup) => void;
}) {
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);

  const isEdit = !!existing;

  const _validate = Yup.object().shape({
    name: Yup.string().required("Batch name is required."),
    committedCount: Yup.number().nullable().min(0, "Must be zero or more."),
  });

  const submit = async (values: any) => {
    setSubmitting(true);
    const payload = {
      name: values.name,
      committedCount: values.committedCount ? Number(values.committedCount) : 0,
      committedAt: values.committedAt
        ? moment.unix(values.committedAt).toISOString()
        : new Date().toISOString(),
      targetDate: values.targetDate
        ? moment.unix(values.targetDate).format("YYYY-MM-DD")
        : null,
      notes: values.notes || null,
    };

    // Optional chaining rather than a non-null assertion: the lint rule
    // @typescript-eslint/no-non-null-assertion is CI-fatal. `isEdit`
    // already proves `existing` is set on this branch.
    const res = isEdit
      ? await updatePlanGroup({
          data: { planId, groupId: existing?.id },
          update: payload,
        })
      : await createPlanGroup({
          data: { planId },
          create: payload,
        });
    setSubmitting(false);

    if (res?.id) {
      toast.add(isEdit ? "Batch updated." : "Batch created.", "var(--theme-ui-colors-success)");
      onSaved(res);
    } else {
      toast.add(
        res?.message || `Error ${isEdit ? "updating" : "creating"} batch.`,
        "var(--theme-ui-colors-danger)",
      );
    }
  };

  return (
    <Modal isOpen={true} toggle={onClose} size="md" centered>
      <ModalHeader toggle={onClose}>{isEdit ? "Edit Batch" : "New Batch"}</ModalHeader>
      <Formik
        initialValues={{
          name: existing?.name ?? "",
          committedCount: existing?.committedCount ?? "",
          committedAt: existing?.committedAt ? moment(existing.committedAt).unix() : undefined,
          targetDate: existing?.targetDate ? moment(existing.targetDate).unix() : undefined,
          notes: existing?.notes ?? "",
        }}
        validationSchema={_validate}
        onSubmit={submit}
      >
        {({ values, errors, submitCount, setFieldValue }) => (
          <Form noValidate autoComplete="off">
            <ModalBody>
              <TextInput
                name="name"
                label="Batch Name"
                required
                value={values.name}
                onChange={(v: any) => setFieldValue("name", v)}
                $errors={errors.name && submitCount > 0 ? errors.name : null}
                placeholder="e.g. FY27 Batch 1"
              />
              <Box sx={{ marginTop: "16px" }}>
                <TextInput
                  type="number"
                  name="committedCount"
                  label="Committed Location Count"
                  min={0}
                  value={values.committedCount}
                  onChange={(v: any) => setFieldValue("committedCount", v)}
                  placeholder="e.g. 125"
                />
                <Subtext
                  fontSize={"11px"}
                  fontFamily={"Poppins"}
                  margin={0}
                  marginTop={"4px"}
                  color={mnt.textMuted}
                >
                  What the client committed to. Allocate the actual Locations from the batch card. This number stays as the contractual target, so any gap between it and the allocated count is visible.
                </Subtext>
              </Box>
              <Grid columns={[1, 2]} gap={3} sx={{ marginTop: "16px" }}>
                <SelectDate
                  name="committedAt"
                  label="Committed On"
                  value={values.committedAt}
                  onChange={(v: any) => setFieldValue("committedAt", v)}
                />
                <SelectDate
                  name="targetDate"
                  label="Target Date"
                  value={values.targetDate}
                  onChange={(v: any) => setFieldValue("targetDate", v)}
                />
              </Grid>
              <Box sx={{ marginTop: "16px" }}>
                <TextInput
                  type="textarea"
                  name="notes"
                  label="Notes"
                  rows={2}
                  value={values.notes}
                  onChange={(v: any) => setFieldValue("notes", v)}
                  placeholder="Optional context."
                />
              </Box>
            </ModalBody>
            <ModalFooter>
              <BasicButton type="button" outline scheme="secondary" onClick={onClose}>
                Cancel
              </BasicButton>
              <BasicButton
                type="submit"
                $submitting={submitting}
                $iconHeight="14px"
                $iconWidth="14px"
                $loadingColor="#fff"
                keepText={true}
              >
                {!submitting && <CheckMarkIcon fill={"#fff"} />}
                {isEdit ? "Save Batch" : "Create Batch"}
              </BasicButton>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}
